import React, { useState } from 'react';
import { SearchIcon, HomeIcon, ChevronRightIcon, FileTextIcon, BuildingIcon, FileSearchIcon, ClipboardCheckIcon, FilterIcon, PlusIcon, DownloadIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { ServiceSelectionModal } from '../components/ServiceSelectionModal';
import { useNavigate } from 'react-router-dom';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Land Title Services
{
  id: 'lt1',
  title: 'New Land Title Registration',
  description: 'Apply for first-time registration of land title',
  category: 'Land Title Services',
  estimatedTime: '15-20 business days',
  fee: '$250.00'
}, {
  id: 'lt2',
  title: 'Land Title Transfer',
  description: 'Transfer land ownership through sale, inheritance, or gift',
  category: 'Land Title Services',
  estimatedTime: '10-15 business days',
  fee: '$200.00'
}, {
  id: 'lt3',
  title: 'Title Rectification',
  description: 'Apply for correction of land title information',
  category: 'Land Title Services',
  estimatedTime: '5-10 business days',
  fee: '$150.00'
}, {
  id: 'lt4',
  title: 'Land Sub-division',
  description: 'Application for subdividing land title',
  category: 'Land Title Services',
  estimatedTime: '20-25 business days',
  fee: '$300.00'
}, {
  id: 'lt5',
  title: 'Land Amalgamation',
  description: 'Combine multiple land titles into one',
  category: 'Land Title Services',
  estimatedTime: '20-25 business days',
  fee: '$300.00'
}, {
  id: 'lt6',
  title: 'Lease Registration',
  description: 'Register a new land lease agreement',
  category: 'Land Title Services',
  estimatedTime: '7-10 business days',
  fee: '$175.00'
},
// Property Development
{
  id: 'pd1',
  title: 'Building Permit - New Construction',
  description: 'Apply for permit to construct new buildings',
  category: 'Property Development',
  estimatedTime: '15-20 business days',
  fee: '$500.00'
}, {
  id: 'pd2',
  title: 'Renovation Permit',
  description: 'Get approval for property renovations or extensions',
  category: 'Property Development',
  estimatedTime: '10-15 business days',
  fee: '$250.00'
}, {
  id: 'pd3',
  title: 'Demolition Permit',
  description: 'Apply for permission to demolish existing structures',
  category: 'Property Development',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
},
// Information Requests
{
  id: 'ir1',
  title: 'Official Land Search',
  description: 'Request official land title verification',
  category: 'Information Requests',
  estimatedTime: '2-3 business days',
  fee: '$50.00'
}, {
  id: 'ir2',
  title: 'Cadastral Map Request',
  description: 'Get detailed property boundary information',
  category: 'Information Requests',
  estimatedTime: '3-5 business days',
  fee: '$75.00'
}, {
  id: 'ir3',
  title: 'Survey Data Request',
  description: 'Access property survey information',
  category: 'Information Requests',
  estimatedTime: '3-5 business days',
  fee: '$100.00'
},
// Permits & Certificates
{
  id: 'pc1',
  title: 'Occupancy Certificate',
  description: 'Apply for building occupancy certification',
  category: 'Permits & Certificates',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
}, {
  id: 'pc2',
  title: 'Hoarding Permit',
  description: 'Get permission for temporary construction barriers',
  category: 'Permits & Certificates',
  estimatedTime: '3-5 business days',
  fee: '$100.00'
}, {
  id: 'pc3',
  title: 'Property Address Assignment',
  description: 'Request official property address confirmation',
  category: 'Permits & Certificates',
  estimatedTime: '5-7 business days',
  fee: '$50.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Land Title Services':
      return <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-3885" />;
    case 'Property Development':
      return <BuildingIcon className="h-6 w-6 text-purple-600" data-id="element-3886" />;
    case 'Information Requests':
      return <FileSearchIcon className="h-6 w-6 text-green-600" data-id="element-3887" />;
    case 'Permits & Certificates':
      return <ClipboardCheckIcon className="h-6 w-6 text-orange-600" data-id="element-3888" />;
    default:
      return <HomeIcon className="h-6 w-6 text-gray-600" data-id="element-3889" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3890">
      <div className="p-5 flex flex-col h-full" data-id="element-3891">
        <div className="flex-1" data-id="element-3892">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3893">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3894">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3895">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3896">
            <div className="flex items-center" data-id="element-3897">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3898">
                ⏱
              </span>
              <span data-id="element-3899">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3900">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3901">
                💰
              </span>
              <span data-id="element-3902">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3903">
            Start Application
            <span className="ml-1" data-id="element-3904">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3905">
      <div className="flex items-center mb-4" data-id="element-3906">
        <CategoryIcon category={category} data-id="element-3907" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3908">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3909">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3910" />)}
      </div>
    </div>;
};
interface ServiceRequest {
  id: string;
  serviceType: string;
  category: string;
  requestDate: string;
  completionDate: string | null;
  status: 'completed' | 'pending' | 'cancelled' | 'in-progress';
  reference: string;
  fee: string;
  assignedTo: string;
  applicantName: string; // Add this field
}
const serviceRequests: ServiceRequest[] = [{
  id: '1',
  serviceType: 'New Land Title Registration',
  category: 'Land Title Services',
  requestDate: '2024-01-15',
  completionDate: '2024-01-20',
  status: 'completed',
  reference: 'LT-2024-001',
  fee: '$250.00',
  assignedTo: 'John Smith',
  applicantName: 'Samuel Bekele'
}, {
  id: '2',
  serviceType: 'Building Permit Application',
  category: 'Property Development',
  requestDate: '2024-01-18',
  completionDate: null,
  status: 'in-progress',
  reference: 'BP-2024-002',
  fee: '$500.00',
  assignedTo: 'Maria Garcia',
  applicantName: 'Tigist Haile'
}, {
  id: '3',
  serviceType: 'Land Title Transfer',
  category: 'Land Title Services',
  requestDate: '2024-01-10',
  completionDate: '2024-01-12',
  status: 'completed',
  reference: 'LT-2024-003',
  fee: '$200.00',
  assignedTo: 'David Chen',
  applicantName: 'Abebe Kebede'
}, {
  id: '4',
  serviceType: 'Property Survey Request',
  category: 'Information Requests',
  requestDate: '2024-01-22',
  completionDate: null,
  status: 'pending',
  reference: 'IR-2024-004',
  fee: '$150.00',
  assignedTo: 'Sarah Johnson',
  applicantName: 'Meseret Tadesse'
}, {
  id: '5',
  serviceType: 'Demolition Permit',
  category: 'Property Development',
  requestDate: '2024-01-08',
  completionDate: null,
  status: 'cancelled',
  reference: 'BP-2024-005',
  fee: '$300.00',
  assignedTo: 'Michael Brown',
  applicantName: 'Dawit Mulugeta'
}, {
  id: '6',
  serviceType: 'Occupancy Certificate',
  category: 'Permits & Certificates',
  requestDate: '2024-01-25',
  completionDate: null,
  status: 'in-progress',
  reference: 'PC-2024-006',
  fee: '$175.00',
  assignedTo: 'Emma Wilson',
  applicantName: 'Alemayehu Negash'
}, {
  id: '7',
  serviceType: 'Land Use Verification',
  category: 'Information Requests',
  requestDate: '2024-01-19',
  completionDate: '2024-01-21',
  status: 'completed',
  reference: 'IR-2024-007',
  fee: '$75.00',
  assignedTo: 'James Taylor',
  applicantName: 'Hanna Girma'
}, {
  id: '8',
  serviceType: 'Property Boundary Amendment',
  category: 'Land Title Services',
  requestDate: '2024-01-16',
  completionDate: null,
  status: 'in-progress',
  reference: 'LT-2024-008',
  fee: '$400.00',
  assignedTo: 'Lisa Anderson',
  applicantName: 'Yonas Tefera'
}, {
  id: '9',
  serviceType: 'Construction Extension Permit',
  category: 'Property Development',
  requestDate: '2024-01-20',
  completionDate: null,
  status: 'pending',
  reference: 'BP-2024-009',
  fee: '$250.00',
  assignedTo: 'Robert Martinez',
  applicantName: 'Selam Assefa'
}, {
  id: '10',
  serviceType: 'Historical Property Record',
  category: 'Information Requests',
  requestDate: '2024-01-14',
  completionDate: '2024-01-17',
  status: 'completed',
  reference: 'IR-2024-010',
  fee: '$100.00',
  assignedTo: 'Amanda White',
  applicantName: 'Bereket Worku'
}];
const StatusBadge = ({
  status
}: {
  status: ServiceRequest['status'];
}) => {
  const styles = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  const icons = {
    completed: <CheckCircleIcon className="w-4 h-4" data-id="element-3911" />,
    pending: <ClockIcon className="w-4 h-4" data-id="element-3912" />,
    'in-progress': <AlertCircleIcon className="w-4 h-4" data-id="element-3913" />,
    cancelled: <XCircleIcon className="w-4 h-4" data-id="element-3914" />
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-3915">
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>;
};
export const LandPropertyPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const handleReferenceClick = (request: ServiceRequest) => {
    navigate('/request-details', {
      state: {
        request
      }
    });
  };
  const handleNewServiceRequest = () => {
    setIsModalOpen(true);
  };
  const handleServiceSelect = (service: Service) => {
    // Here you would typically:
    // 1. Create a new service request
    // 2. Navigate to a form or the next step
    // For now, we'll just close the modal
    setIsModalOpen(false);
    // You can add additional logic here
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedCategory('all');
    setDateRange({
      from: '',
      to: ''
    });
    setCurrentPage(1);
  };
  const filteredRequests = serviceRequests.filter(request => {
    const matchesSearch = request.serviceType.toLowerCase().includes(searchQuery.toLowerCase()) || request.reference.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || request.status === selectedStatus;
    const matchesCategory = selectedCategory === 'all' || request.category === selectedCategory;
    // Add date filtering logic if dates are selected
    let matchesDateRange = true;
    if (dateRange.from) {
      matchesDateRange = matchesDateRange && new Date(request.requestDate) >= new Date(dateRange.from);
    }
    if (dateRange.to) {
      matchesDateRange = matchesDateRange && new Date(request.requestDate) <= new Date(dateRange.to);
    }
    return matchesSearch && matchesStatus && matchesCategory && matchesDateRange;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  return <div className="min-h-screen bg-gray-50" data-id="element-3916">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3917">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3918">
          <a href="/" className="hover:text-gray-700" data-id="element-3919">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3920" />
          <span className="text-gray-900" data-id="element-3921">Land & Property Services</span>
        </div>

        {/* Header */}
        <div className="mb-8" data-id="element-3922">
          <div className="flex justify-between items-start" data-id="element-3923">
            <div data-id="element-3924">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3925">
                Service Requests
              </h1>
              <p className="text-gray-600" data-id="element-3926">
                View and manage your land & property service requests
              </p>
            </div>
            <div className="flex gap-3" data-id="element-3927">
              <button onClick={() => navigate('/assisted-creation/identify-citizen/new')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-3928">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-3929" />
                New Service Request
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-3930">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3931" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6" data-id="element-3932">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3933">
            <div data-id="element-3934">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3935">
                Search
              </label>
              <div className="relative" data-id="element-3936">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3937" />
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search requests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3938" />
              </div>
            </div>
            <div data-id="element-3939">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3940">
                Status
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-3941">
                <option value="all" data-id="element-3942">All Statuses</option>
                <option value="completed" data-id="element-3943">Completed</option>
                <option value="in-progress" data-id="element-3944">In Progress</option>
                <option value="pending" data-id="element-3945">Pending</option>
                <option value="cancelled" data-id="element-3946">Cancelled</option>
              </select>
            </div>
            <div data-id="element-3947">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3948">
                Category
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-3949">
                <option value="all" data-id="element-3950">All Categories</option>
                <option value="Land Title Services" data-id="element-3951">Land Title Services</option>
                <option value="Property Development" data-id="element-3952">
                  Property Development
                </option>
                <option value="Information Requests" data-id="element-3953">
                  Information Requests
                </option>
                <option value="Permits & Certificates" data-id="element-3954">
                  Permits & Certificates
                </option>
              </select>
            </div>
            <div data-id="element-3955">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3956">
                Date Range
              </label>
              <div className="flex flex-col space-y-2" data-id="element-3957">
                <div className="flex gap-2" data-id="element-3958">
                  <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={dateRange.from} onChange={e => setDateRange({
                  ...dateRange,
                  from: e.target.value
                })} data-id="element-3959" />
                  <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={dateRange.to} onChange={e => setDateRange({
                  ...dateRange,
                  to: e.target.value
                })} data-id="element-3960" />
                </div>
                <div className="flex gap-2" data-id="element-3961">
                  <button onClick={() => setCurrentPage(1)} className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-3962">
                    Apply Filter
                  </button>
                  <button onClick={handleClearFilters} className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3963">
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Requests Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-3964">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-3965">
            <thead className="bg-gray-50" data-id="element-3966">
              <tr data-id="element-3967">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3968">
                  Reference
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3969">
                  Applicant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3970">
                  Service Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3971">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3972">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3973">
                  Request Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3974">
                  Fee
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3975">
              {paginatedRequests.map(request => <tr key={request.id} className="hover:bg-gray-50" data-id="element-3976">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-3977">
                    <button onClick={() => handleReferenceClick(request)} className="hover:underline focus:outline-none" data-id="element-3978">
                      {request.reference}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3979">
                    {request.applicantName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3980">
                    {request.serviceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3981">
                    {request.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3982">
                    <StatusBadge status={request.status} data-id="element-3983" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3984">
                    {new Date(request.requestDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3985">
                    {request.fee}
                  </td>
                </tr>)}
              {/* Add empty rows if needed to maintain consistent height */}
              {paginatedRequests.length < rowsPerPage && Array(rowsPerPage - paginatedRequests.length).fill(0).map((_, index) => <tr key={`empty-${index}`} data-id="element-3986">
                      <td colSpan={7} className="px-6 py-4" data-id="element-3987">
                        &nbsp;
                      </td>
                    </tr>)}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-3988">
            <div className="flex-1 flex justify-between sm:hidden" data-id="element-3989">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3990">
                Previous
              </button>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3991">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-3992">
              <div data-id="element-3993">
                <p className="text-sm text-gray-700" data-id="element-3994">
                  Showing{' '}
                  <span className="font-medium" data-id="element-3995">
                    {filteredRequests.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium" data-id="element-3996">
                    {Math.min(currentPage * rowsPerPage, filteredRequests.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium" data-id="element-3997">{filteredRequests.length}</span>{' '}
                  results
                </p>
              </div>
              <div data-id="element-3998">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-3999">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4000">
                    <span className="sr-only" data-id="element-4001">Previous</span>
                    <ChevronRightIcon className="h-5 w-5 transform rotate-180" data-id="element-4002" />
                  </button>
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-4003">
                      {i + 1}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4004">
                    <span className="sr-only" data-id="element-4005">Next</span>
                    <ChevronRightIcon className="h-5 w-5" data-id="element-4006" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Service Selection Modal */}
        <ServiceSelectionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onServiceSelect={handleServiceSelect} data-id="element-4007" />

        {/* Request Detail Modal */}
        {selectedRequest && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-4008">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-4009">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-4010">
                <div className="flex items-center justify-between" data-id="element-4011">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-4012">
                    Service Request Details
                  </h3>
                  <button onClick={() => setSelectedRequest(null)} className="text-gray-400 hover:text-gray-500" data-id="element-4013">
                    <XCircleIcon className="h-5 w-5" data-id="element-4014" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4" data-id="element-4015">
                <div className="grid grid-cols-2 gap-4" data-id="element-4016">
                  <div data-id="element-4017">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4018">
                      Reference
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4019">
                      {selectedRequest.reference}
                    </p>
                  </div>
                  <div data-id="element-4020">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4021">
                      Status
                    </label>
                    <p className="mt-1" data-id="element-4022">
                      <StatusBadge status={selectedRequest.status} data-id="element-4023" />
                    </p>
                  </div>
                  <div data-id="element-4024">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4025">
                      Applicant
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4026">
                      {selectedRequest.applicantName}
                    </p>
                  </div>
                  <div data-id="element-4027">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4028">
                      Service Type
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4029">
                      {selectedRequest.serviceType}
                    </p>
                  </div>
                  <div data-id="element-4030">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4031">
                      Category
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4032">
                      {selectedRequest.category}
                    </p>
                  </div>
                  <div data-id="element-4033">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4034">
                      Fee
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4035">
                      {selectedRequest.fee}
                    </p>
                  </div>
                  <div data-id="element-4036">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4037">
                      Request Date
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4038">
                      {new Date(selectedRequest.requestDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div data-id="element-4039">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4040">
                      Completion Date
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4041">
                      {selectedRequest.completionDate ? new Date(selectedRequest.completionDate).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <div data-id="element-4042">
                    <label className="text-sm font-medium text-gray-500" data-id="element-4043">
                      Assigned To
                    </label>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-4044">
                      {selectedRequest.assignedTo}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-4045">
                <button onClick={() => setSelectedRequest(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4046">
                  Close
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};